import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/apiClient';
import type { DataResponse } from '../types/api';
import { useAuth } from './AuthContext';
import { useProfile, profileQueryKey } from './useProfile';
import type { UserProfile } from './userProfile';

/** Tables with persisted column visibility. Single FE source; the type derives
 *  from it. Mirror discovr-backend's knownColumnPrefTables (pinned by a test on
 *  each side). */
export const COLUMN_TABLE_IDS = ['creators', 'content', 'scripts', 'confirmed', 'live'] as const;

export type ColumnTableId = (typeof COLUMN_TABLE_IDS)[number];

/** Columns-menu state — same shape as DataTable's useColumnVisibilityState(). */
export interface ColumnPreferences {
  hiddenIds: ReadonlySet<string>;
  toggle: (id: string) => void;
  reset: () => void;
}

/** Optional hooks for the persisted (authenticated) path. */
export interface UseColumnPreferencesOptions {
  /** Called after the optimistic update is rolled back on a failed PUT, so the
   *  UI layer (which owns the toast library) can surface the failure. Kept out
   *  of this hook so `@discovr/common` stays free of a UI dependency. */
  onError?: (error: unknown) => void;
}

/** Full next hidden set after flipping `id`. Pure — no render-state closure. */
function nextHiddenSet(hidden: ReadonlySet<string>, id: string): string[] {
  const next = new Set(hidden);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return Array.from(next);
}

/**
 * Per-user, per-campaign column visibility for one DataTable, persisted via
 * PUT /profile/campaigns/{campaignId}/column-preferences/{tableId} with an
 * optimistic profile-cache update and rollback. Falls back to in-memory state
 * when unauthenticated or when there's no campaign (the public share view has
 * no user and the PUT would 401; a missing campaignId can't be persisted).
 */
export function useColumnPreferences(
  campaignId: string | null | undefined,
  tableId: ColumnTableId,
  options?: UseColumnPreferencesOptions,
): ColumnPreferences {
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const queryClient = useQueryClient();

  // Stable identity (consumers memoize on it): the persisted array reference is
  // stable across renders until the data actually changes.
  const persisted = campaignId ? profile?.campaign_column_preferences?.[campaignId]?.[tableId] : undefined;
  const persistedHidden = useMemo<ReadonlySet<string>>(() => new Set(persisted ?? []), [persisted]);

  // Working copy of the hidden set while writes are in flight, plus an in-flight
  // counter. A burst of toggles must compose on the latest intended state, but
  // onMutate only writes the optimistic cache AFTER awaiting cancelQueries — so
  // two synchronous toggles would otherwise both read the same pre-mutation
  // snapshot and their PUTs would clobber each other, silently dropping one.
  // pendingRef is updated synchronously in the toggle itself; it's cleared once
  // the last in-flight write settles, after which we read from the cache again.
  const pendingRef = useRef<ReadonlySet<string> | null>(null);
  const inFlightRef = useRef(0);

  // The working copy is scoped to one (campaign, table). Consumers stay mounted
  // across a campaign switch (tabs just get a new campaignId prop), so a stale
  // working set from the previous scope must not compose onto the new scope's
  // toggles — drop it when the scope changes. inFlightRef is deliberately NOT
  // reset: it's a scope-agnostic counter of outstanding writes, and clearing it
  // mid-flight would desync the onSettled accounting.
  const scopeKey = `${campaignId ?? ''}::${tableId}`;
  const scopeKeyRef = useRef(scopeKey);
  if (scopeKeyRef.current !== scopeKey) {
    scopeKeyRef.current = scopeKey;
    pendingRef.current = null;
  }

  const mutation = useMutation({
    mutationFn: (hidden: string[]) =>
      apiClient
        .put<DataResponse<{ hidden_columns: string[] }>>(
          `/profile/campaigns/${campaignId}/column-preferences/${tableId}`,
          { hidden_columns: hidden },
        )
        .then((r) => r.data.data),

    onMutate: async (hidden: string[]) => {
      inFlightRef.current += 1;
      const key = profileQueryKey(user?.uid);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<UserProfile>(key);
      queryClient.setQueryData<UserProfile | undefined>(key, (old) => {
        if (!old || !campaignId) return old;
        const campaigns = old.campaign_column_preferences ?? {};
        const tables = campaigns[campaignId] ?? {};
        return {
          ...old,
          campaign_column_preferences: { ...campaigns, [campaignId]: { ...tables, [tableId]: hidden } },
        };
      });
      return { previous };
    },

    onError: (err, _hidden, context) => {
      if (context?.previous) {
        queryClient.setQueryData(profileQueryKey(user?.uid), context.previous);
      }
      options?.onError?.(err);
    },

    onSettled: () => {
      inFlightRef.current -= 1;
      // Only drop the working copy once every queued write has settled, so a
      // burst still composes; then reconcile with the server.
      if (inFlightRef.current === 0) {
        pendingRef.current = null;
        void queryClient.invalidateQueries({ queryKey: profileQueryKey(user?.uid) });
      }
    },
  });

  const { mutate } = mutation;

  // Compose on the in-flight working copy when a burst is mid-flight, else on
  // the freshly persisted set from the cache — never on a stale render closure.
  const persistedToggle = useCallback(
    (id: string): void => {
      const base =
        pendingRef.current ??
        (campaignId
          ? queryClient.getQueryData<UserProfile>(profileQueryKey(user?.uid))?.campaign_column_preferences?.[campaignId]?.[
              tableId
            ] ?? []
          : []);
      const next = nextHiddenSet(new Set(base), id);
      pendingRef.current = new Set(next);
      mutate(next);
    },
    [queryClient, user?.uid, campaignId, tableId, mutate],
  );

  const persistedReset = useCallback((): void => {
    pendingRef.current = new Set<string>();
    mutate([]);
  }, [mutate]);

  // In-memory fallback for the unauthenticated public view.
  const [localHidden, setLocalHidden] = useState<ReadonlySet<string>>(() => new Set());

  const localToggle = useCallback(
    (id: string): void => setLocalHidden((prev) => new Set(nextHiddenSet(prev, id))),
    [],
  );

  const localReset = useCallback((): void => setLocalHidden(new Set()), []);

  // Persist only when we have both a user and a campaign to scope under;
  // otherwise (public share view, or no campaign) keep column state in memory.
  const isAuthenticated = user != null && !!campaignId;

  return useMemo<ColumnPreferences>(() => {
    if (isAuthenticated) {
      return { hiddenIds: persistedHidden, toggle: persistedToggle, reset: persistedReset };
    }
    return { hiddenIds: localHidden, toggle: localToggle, reset: localReset };
  }, [
    isAuthenticated,
    persistedHidden,
    persistedToggle,
    persistedReset,
    localHidden,
    localToggle,
    localReset,
  ]);
}
